import { useState, useEffect, useCallback } from 'react';
import i18n from '../i18n/config';
import { createEventMasterTheme } from '../theme';

export type ThemeMode = 'light' | 'dark' | 'system';

interface UseThemeReturn {
  mode: ThemeMode;
  actualMode: 'light' | 'dark';
  theme: ReturnType<typeof createEventMasterTheme>;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const THEME_STORAGE_KEY = 'eventos-theme-mode';

export const useTheme = (): UseThemeReturn => {
  // Получаем сохраненную тему или используем system по умолчанию
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return (saved as ThemeMode) || 'system';
  });

  // Определяем системную тему
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Следим за изменением языка i18n для обновления MUI-локали
  const [language, setLanguage] = useState<string>(
    () => (i18n.language || 'ru').split('-')[0] ?? 'ru'
  );

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng.split('-')[0] ?? 'ru');
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  // Вычисляем актуальную тему
  const actualMode = mode === 'system' ? systemTheme : mode;

  // Создаем тему MUI с локалью текущего языка
  const theme = createEventMasterTheme(actualMode, language);

  // Функция для установки темы
  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(THEME_STORAGE_KEY, newMode);
    
    // Устанавливаем data-theme атрибут для CSS переменных
    if (newMode === 'system') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', newMode);
    }
  }, []);

  // Функция для переключения темы
  const toggleMode = useCallback(() => {
    if (mode === 'light') {
      setMode('dark');
    } else if (mode === 'dark') {
      setMode('system');
    } else {
      setMode('light');
    }
  }, [mode, setMode]);

  // Слушаем изменения системной темы
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Устанавливаем data-theme атрибут при инициализации
  useEffect(() => {
    if (mode === 'system') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', mode);
    }
  }, [mode]);

  // Обновляем meta theme-color для мобильных браузеров
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const color = actualMode === 'dark' ? '#212121' : '#fafafa';
    
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', color);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = color;
      document.head.appendChild(meta);
    }
  }, [actualMode]);

  return {
    mode,
    actualMode,
    theme,
    setMode,
    toggleMode,
  };
}; 